import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Button, Radio, Typography,
} from '@mui/material';
import { CompareArrows, Devices } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import {
  SonosSpeaker, SavedSonosGroup, liveDestinationApply, sonosControl,
  resolveCoordinatorSpeaker, resolveDestinationGroup,
  fetchSavedSonosGroups, speakerIdsKey,
} from '../../services/sonosApi';
import { refrescarSonos, mergeSonosSpeakers } from '../../services/sonosState';
import { useManualTouchScroll, TOUCH_SCROLL_PANEL_SX } from '../../hooks/useManualTouchScroll';
import { HEADER_GRADIENT, MUTED, SONOS_BLUE, SURFACE_BORDER } from './sonosUiStyles';
import SonosModelBadge, { SONOS_MODEL_BADGE_SIZE } from './SonosModelBadge';
import ThumbOnlyVolumeSlider from './ThumbOnlyVolumeSlider';
import RoundSonosCheckbox from './RoundSonosCheckbox';
import SonosSideSlidePanel, { SonosSideSlidePanelHandle } from './SonosSideSlidePanel';
import type { PlaybackDestinationKind } from './playbackDestination';

export type LiveDestinationApplyResult =
  | { kind: 'local' }
  | { kind: 'sonos'; coordinatorId: number | null };

const PILL_BTN_SX = {
  borderRadius: 999,
  textTransform: 'none' as const,
  fontSize: 12,
  fontWeight: 600,
  minHeight: 40,
  py: 0.75,
};

interface DestinationVolumeProps {
  volume: number;
  disabled?: boolean;
  onApplied: (volume: number) => void;
}

const DestinationVolume: React.FC<DestinationVolumeProps> = ({
  volume, disabled, onApplied,
}) => (
  <ThumbOnlyVolumeSlider
    volume={volume}
    disabled={disabled}
    trackInsetX={1}
    onVolumeChange={onApplied}
    onVolumeCommit={onApplied}
  />
);

interface LiveDestinationModalProps {
  open: boolean;
  onClose: () => void;
  speakers: SonosSpeaker[];
  initialCoordinatorId: number | null;
  /** Destino inicial al abrir el modal. */
  initialKind?: PlaybackDestinationKind;
  /** Si false, oculta «Este dispositivo» (p.ej. vistas fijas a un parlante). Default true. */
  allowLocal?: boolean;
  /**
   * Si se indica, solo se pueden seleccionar parlantes de esa habitación
   * (el resto y «Este dispositivo» quedan visibles pero deshabilitados).
   * Usado al abrir desde tarjeta Sonos de una habitación.
   */
  restrictToHabitacionId?: number | null;
  onApplied: (result: LiveDestinationApplyResult, speakers?: SonosSpeaker[]) => void | Promise<void>;
}

const LiveDestinationModal: React.FC<LiveDestinationModalProps> = ({
  open,
  onClose,
  speakers,
  initialCoordinatorId,
  initialKind = 'sonos',
  allowLocal = true,
  restrictToHabitacionId = null,
  onApplied,
}) => {
  const { t } = useTranslation();
  const panelRef = useRef<SonosSideSlidePanelHandle>(null);
  const speakerScrollRef = useManualTouchScroll(open);
  const roomRestricted = restrictToHabitacionId != null;
  const localSelectable = allowLocal && !roomRestricted;
  const controllable = useMemo(
    () => speakers
      .filter((s) => s.can_control)
      .sort((a, b) => a.nombre.localeCompare(b.nombre, 'es', { sensitivity: 'base' })),
    [speakers],
  );
  const selectable = useMemo(
    () => (roomRestricted
      ? controllable.filter((s) => s.habitacion_id === restrictToHabitacionId)
      : controllable),
    [controllable, roomRestricted, restrictToHabitacionId],
  );
  const selectableIds = useMemo(() => selectable.map((s) => s.id), [selectable]);
  const selectableIdSet = useMemo(() => new Set(selectableIds), [selectableIds]);
  const [savedGroups, setSavedGroups] = useState<SavedSonosGroup[]>([]);

  // Prefetch al montar (el modal vive en el player): evita flash de chips al abrir.
  useEffect(() => {
    let cancelled = false;
    fetchSavedSonosGroups()
      .then((groups) => { if (!cancelled) setSavedGroups(groups); })
      .catch(() => { /* conservar cache previa */ });
    return () => { cancelled = true; };
  }, []);

  const [selected, setSelected] = useState<number[]>([]);
  const [coordinatorId, setCoordinatorId] = useState<number | null>(null);
  const [destKind, setDestKind] = useState<PlaybackDestinationKind>(initialKind);
  const [localSpeakers, setLocalSpeakers] = useState<SonosSpeaker[]>(speakers);
  const [busy, setBusy] = useState(false);
  const [applyError, setApplyError] = useState<string | null>(null);
  const didInitForOpenRef = useRef(false);
  const userTouchedRef = useRef(false);
  const hadSpeakersRef = useRef(false);
  const baselineCoordinatorIdRef = useRef<number | null>(null);
  const baselineSpeakerIdsRef = useRef<number[]>([]);
  const volumeTouchedIdsRef = useRef<Set<number>>(new Set());

  const effectiveCoordinatorId = coordinatorId
    ?? (selected.length === 1 ? selected[0] : null)
    ?? baselineCoordinatorIdRef.current;
  const canApply = destKind === 'local'
    ? localSelectable
    : selected.length === 0
      ? effectiveCoordinatorId != null && selectableIdSet.has(effectiveCoordinatorId)
      : selected.length > 0
        && selected.every((id) => selectableIdSet.has(id))
        && (
          selected.length === 1
          || (coordinatorId != null && selected.includes(coordinatorId))
        );

  const allSelected = selectableIds.length > 0
    && selectableIds.every((id) => selected.includes(id));

  const isSpeakerSelectable = useCallback((sp: SonosSpeaker) => (
    sp.can_control && selectableIdSet.has(sp.id)
  ), [selectableIdSet]);

  const isSavedGroupAllowed = useCallback((group: SavedSonosGroup) => {
    if (!roomRestricted) return true;
    if (!group.speaker_ids.length) return false;
    return group.speaker_ids.every((id) => selectableIdSet.has(id));
  }, [roomRestricted, selectableIdSet]);

  useEffect(() => {
    if (open) setApplyError(null);
  }, [open]);

  // Init antes del paint al abrir: un solo commit (evita titileo lista/selección).
  useLayoutEffect(() => {
    if (!open) {
      setBusy(false);
      didInitForOpenRef.current = false;
      userTouchedRef.current = false;
      hadSpeakersRef.current = false;
      baselineCoordinatorIdRef.current = null;
      baselineSpeakerIdsRef.current = [];
      volumeTouchedIdsRef.current.clear();
      return;
    }
    if (userTouchedRef.current) return;

    const speakersJustArrived = !hadSpeakersRef.current && speakers.length > 0;
    hadSpeakersRef.current = speakers.length > 0;
    if (didInitForOpenRef.current && !speakersJustArrived) return;
    didInitForOpenRef.current = true;

    setLocalSpeakers(speakers.map((s) => ({ ...s })));
    // Si el destino real es «local» pero no se puede elegir aquí, no fingir que ya
    // está aplicado un parlante Sonos (eso mostraba Cocina tildada mientras sonaba el teléfono).
    const kind: PlaybackDestinationKind = localSelectable
      ? initialKind
      : (initialKind === 'local' ? 'sonos' : initialKind);
    setDestKind(kind);
    if (kind === 'local') {
      baselineCoordinatorIdRef.current = null;
      baselineSpeakerIdsRef.current = [];
      setCoordinatorId(null);
      setSelected([]);
      return;
    }
    if (initialKind === 'local' && !localSelectable) {
      // Borrador: sugerir parlante de la habitación / coordinator, sin dar a entender
      // que el audio ya está ahí hasta que el usuario pulse Aplicar.
      const suggested = (
        initialCoordinatorId != null && selectableIdSet.has(initialCoordinatorId)
          ? initialCoordinatorId
          : selectableIds[0] ?? null
      );
      baselineCoordinatorIdRef.current = suggested;
      baselineSpeakerIdsRef.current = suggested != null ? [suggested] : [];
      setCoordinatorId(suggested);
      setSelected(suggested != null ? [suggested] : []);
      return;
    }
    if (initialCoordinatorId == null && !roomRestricted) {
      baselineCoordinatorIdRef.current = null;
      baselineSpeakerIdsRef.current = [];
      setCoordinatorId(null);
      setSelected([]);
      return;
    }

    const resolvedCoord = resolveCoordinatorSpeaker(speakers, initialCoordinatorId);
    const group = resolveDestinationGroup(
      speakers,
      resolvedCoord?.id ?? initialCoordinatorId,
    );
    let groupIds = group.length > 0
      ? group.map((s) => s.id)
      : (initialCoordinatorId != null ? [initialCoordinatorId] : []);
    if (roomRestricted) {
      groupIds = groupIds.filter((id) => selectableIdSet.has(id));
      if (groupIds.length === 0) {
        if (initialCoordinatorId != null && selectableIdSet.has(initialCoordinatorId)) {
          groupIds = [initialCoordinatorId];
        } else if (selectableIds[0] != null) {
          groupIds = [selectableIds[0]];
        }
      }
    }
    const init = (
      resolvedCoord != null && selectableIdSet.has(resolvedCoord.id)
        ? resolvedCoord.id
        : null
    ) ?? groupIds[0] ?? null;
    baselineCoordinatorIdRef.current = init;
    setCoordinatorId(init);
    baselineSpeakerIdsRef.current = groupIds;
    setSelected(groupIds);
  }, [
    open, initialCoordinatorId, initialKind, localSelectable, roomRestricted,
    speakers, selectableIds, selectableIdSet,
  ]);

  // Sync telemetría sin forzar re-render si los datos relevantes no cambiaron.
  useEffect(() => {
    if (!open || !didInitForOpenRef.current) return;
    setLocalSpeakers((prev) => {
      const next = speakers.map((s) => {
        const prior = prev.find((p) => p.id === s.id);
        if (!prior) return { ...s };
        const volume = userTouchedRef.current && volumeTouchedIdsRef.current.has(s.id)
          ? prior.volume
          : s.volume;
        if (
          prior.volume === volume
          && prior.nombre === s.nombre
          && prior.is_coordinator === s.is_coordinator
          && prior.can_control === s.can_control
          && prior.mute === s.mute
        ) {
          return prior;
        }
        return { ...s, volume };
      });
      if (next.length === prev.length && next.every((s, i) => s === prev[i])) return prev;
      return next;
    });
  }, [open, speakers]);

  const selectLocalDevice = () => {
    if (!localSelectable) return;
    userTouchedRef.current = true;
    setDestKind('local');
    setSelected([]);
    setCoordinatorId(null);
  };

  const toggleSelect = (id: number) => {
    if (!selectableIdSet.has(id)) return;
    userTouchedRef.current = true;
    setDestKind('sonos');
    setSelected((prev) => {
      const next = prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id];
      if (next.length === 0) {
        setCoordinatorId(null);
      } else if (coordinatorId == null || !next.includes(coordinatorId)) {
        setCoordinatorId(next[0] ?? null);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectableIds.length === 0) return;
    userTouchedRef.current = true;
    setDestKind('sonos');
    if (allSelected) {
      setSelected([]);
      setCoordinatorId(null);
      return;
    }
    setSelected(selectableIds);
    setCoordinatorId((prev) => (
      prev != null && selectableIds.includes(prev) ? prev : selectableIds[0] ?? null
    ));
  };

  const applyGroupChip = (group: SavedSonosGroup) => {
    if (!isSavedGroupAllowed(group)) return;
    userTouchedRef.current = true;
    setDestKind('sonos');
    setCoordinatorId(group.coordinator_speaker_id);
    setSelected([...group.speaker_ids]);
    setLocalSpeakers((prev) => prev.map((s) => {
      const savedVol = group.volumes[String(s.id)];
      if (savedVol != null) volumeTouchedIdsRef.current.add(s.id);
      return savedVol != null ? { ...s, volume: savedVol } : s;
    }));
  };

  const isGroupChipActive = (group: SavedSonosGroup) => (
    coordinatorId === group.coordinator_speaker_id
    && speakerIdsKey(group.speaker_ids) === speakerIdsKey(selected)
  );

  const handleNormalize = () => {
    if (selected.length === 0) return;
    const coordId = effectiveCoordinatorId;
    if (coordId == null) return;
    const masterVol = localSpeakers.find((s) => s.id === coordId)?.volume ?? 0;
    selected.forEach((id) => volumeTouchedIdsRef.current.add(id));
    setLocalSpeakers((prev) => prev.map((s) => (
      selected.includes(s.id) ? { ...s, volume: masterVol } : s
    )));
  };

  const handleApply = async () => {
    if (busy) return;
    if (destKind === 'local') {
      if (!localSelectable) return;
      setBusy(true);
      setApplyError(null);
      try {
        setBusy(false);
        panelRef.current?.close();
        await onApplied({ kind: 'local' });
      } catch (e: unknown) {
        const detail = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
        setApplyError(typeof detail === 'string' ? detail : t('musicSystem.liveDestination.applyError'));
        setBusy(false);
      }
      return;
    }

    const coordId = coordinatorId
      ?? (selected.length === 1 ? selected[0] : null)
      ?? baselineCoordinatorIdRef.current;
    if (!coordId) return;
    if (selected.length > 1 && !selected.includes(coordId)) return;
    setBusy(true);
    setApplyError(null);
    try {
      const ids = selected.length === 0
        ? []
        : Array.from(new Set([coordId, ...selected]));
      const cleared = selected.length === 0;
      const volumes: Record<string, number> = {};
      ids.forEach((id) => {
        const isNewMember = !baselineSpeakerIdsRef.current.includes(id);
        const volumeEdited = volumeTouchedIdsRef.current.has(id);
        if (!isNewMember && !volumeEdited) return;
        const local = localSpeakers.find((s) => s.id === id);
        const live = speakers.find((s) => s.id === id);
        volumes[String(id)] = local?.volume ?? live?.volume ?? 0;
      });

      /**
       * Sin destino previo (p.ej. otro usuario abrió Audio y debe elegir parlante):
       * “Aplicar” = enganchar la UI al parlante/grupo que ya existe, sin reagrupar
       * Sonos. Reagrupar cortaría reproducción/cola de quien ya estaba usando la zona.
       */
      const hadNoPriorDestination = (
        baselineCoordinatorIdRef.current == null
        && baselineSpeakerIdsRef.current.length === 0
      );
      if (!cleared && hadNoPriorDestination) {
        const focusId = selected.length === 1 ? selected[0] : coordId;
        const existingGroup = resolveDestinationGroup(speakers, focusId);
        const existingIds = existingGroup.length
          ? existingGroup.map((s) => s.id)
          : [focusId];
        const existingSet = new Set(existingIds);
        const desiredSet = new Set(ids);
        const sameTopology = (
          desiredSet.size === existingSet.size
          && [...desiredSet].every((id) => existingSet.has(id))
        );
        // Un solo parlante tildado → entrar a esa zona con su grupo actual intacto.
        const singlePickAttach = ids.length === 1 && existingSet.has(ids[0]);
        if (sameTopology || singlePickAttach) {
          const attachCoord = (
            resolveCoordinatorSpeaker(speakers, focusId)?.id
            ?? existingGroup[0]?.id
            ?? focusId
          );
          const volumeEntries = Object.entries(volumes);
          if (volumeEntries.length) {
            await Promise.all(volumeEntries.map(([id, vol]) => (
              sonosControl(Number(id), 'volume', vol).catch(() => undefined)
            )));
          }
          setBusy(false);
          panelRef.current?.close();
          void (async () => {
            try {
              await refrescarSonos();
              await onApplied({ kind: 'sonos', coordinatorId: attachCoord });
            } catch {
              /* refresh/prefs best-effort; la UI ya puede engancharse */
            }
          })();
          return;
        }
      }

      // Misma topología que el destino actual → no tocar Sonos (evita cortes).
      if (!cleared && !hadNoPriorDestination) {
        const existingGroup = resolveDestinationGroup(speakers, coordId);
        const existingSet = new Set(
          existingGroup.length ? existingGroup.map((s) => s.id) : [coordId],
        );
        const desiredSet = new Set(ids);
        const sameTopology = (
          desiredSet.size === existingSet.size
          && [...desiredSet].every((id) => existingSet.has(id))
        );
        if (sameTopology) {
          const attachCoord = (
            resolveCoordinatorSpeaker(speakers, coordId)?.id ?? coordId
          );
          const volumeEntries = Object.entries(volumes);
          if (volumeEntries.length) {
            await Promise.all(volumeEntries.map(([id, vol]) => (
              sonosControl(Number(id), 'volume', vol).catch(() => undefined)
            )));
          }
          setBusy(false);
          panelRef.current?.close();
          void (async () => {
            try {
              await refrescarSonos();
              await onApplied({ kind: 'sonos', coordinatorId: attachCoord });
            } catch {
              /* best-effort */
            }
          })();
          return;
        }
      }

      const result = await liveDestinationApply(
        coordId,
        ids,
        cleared ? undefined : volumes,
        baselineCoordinatorIdRef.current,
      );
      if (result.speakers.length) mergeSonosSpeakers(result.speakers);
      // Liberar UI de inmediato: el post-apply (refresh/prefs) no debe dejar
      // el botón en "Aplicando…" si se cuelga o tarda.
      setBusy(false);
      panelRef.current?.close();
      void (async () => {
        try {
          await refrescarSonos();
          await onApplied(
            { kind: 'sonos', coordinatorId: cleared ? null : result.coordinator_id },
            result.speakers,
          );
        } catch {
          /* el grupo ya se aplicó en Sonos; el refresh es best-effort */
        }
      })();
    } catch (e: unknown) {
      const detail = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setApplyError(typeof detail === 'string' ? detail : t('musicSystem.liveDestination.applyError', { defaultValue: 'No se pudo aplicar el grupo.' }));
      setBusy(false);
    }
  };

  const refreshVol = useCallback((volume: number, speakerId: number) => {
    userTouchedRef.current = true;
    volumeTouchedIdsRef.current.add(speakerId);
    setLocalSpeakers((prev) => prev.map((s) => (
      s.id === speakerId ? { ...s, volume } : s
    )));
  }, []);

  const chipBtnSx = {
    ...PILL_BTN_SX,
    minHeight: 32,
    py: 0.35,
    px: 1.25,
    fontSize: 11,
    flexShrink: 0,
    borderColor: SONOS_BLUE,
  };

  const toolbar = destKind === 'local' ? undefined : (
    <Box sx={{
      flexShrink: 0,
      px: 1,
      py: 0.75,
      minHeight: 48,
      borderBottom: `1px solid ${SURFACE_BORDER}`,
      background: HEADER_GRADIENT,
    }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
        <Button
          onClick={toggleSelectAll}
          disabled={selectableIds.length === 0}
          variant="outlined"
          sx={{
            ...chipBtnSx,
            color: allSelected ? '#fff' : SONOS_BLUE,
            backgroundColor: allSelected ? SONOS_BLUE : 'transparent',
            '&:hover': {
              borderColor: '#1a7ae0',
              backgroundColor: allSelected ? '#1a7ae0' : 'rgba(35,145,255,0.08)',
            },
          }}
        >
          {allSelected
            ? t('musicSystem.liveDestination.deselectAll')
            : t('musicSystem.liveDestination.selectAll')}
        </Button>
        {savedGroups.map((group) => {
          const active = isGroupChipActive(group);
          const groupAllowed = isSavedGroupAllowed(group);
          return (
            <Button
              key={`saved-${group.id}`}
              onClick={() => applyGroupChip(group)}
              disabled={!groupAllowed}
              variant={active ? 'contained' : 'outlined'}
              sx={{
                ...chipBtnSx,
                color: active ? '#fff' : SONOS_BLUE,
                backgroundColor: active ? SONOS_BLUE : 'transparent',
                opacity: groupAllowed ? 1 : 0.4,
                '&:hover': {
                  backgroundColor: active ? '#1a7ae0' : 'rgba(35,145,255,0.08)',
                  borderColor: '#1a7ae0',
                },
                '&.Mui-disabled': {
                  color: MUTED,
                  borderColor: SURFACE_BORDER,
                  backgroundColor: 'transparent',
                  opacity: 0.4,
                },
              }}
            >
              {group.name}
            </Button>
          );
        })}
      </Box>
    </Box>
  );

  const speakerList = (
    <Box
      ref={speakerScrollRef}
      data-audio-inner-scroll=""
      sx={{
        ...TOUCH_SCROLL_PANEL_SX,
        flex: 1,
        minHeight: 0,
        height: 'auto',
        px: 1,
        py: 0.75,
      }}
    >
      {allowLocal && (
        <Box
          onClick={selectLocalDevice}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            py: 1,
            px: 0.5,
            mb: 0.75,
            borderBottom: `1px solid ${SURFACE_BORDER}`,
            cursor: localSelectable ? 'pointer' : 'default',
            bgcolor: 'transparent',
            opacity: localSelectable ? 1 : 0.45,
          }}
        >
          <Devices sx={{ color: destKind === 'local' ? SONOS_BLUE : MUTED, fontSize: 28, ml: 0.5 }} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ color: '#fff', fontSize: 13, fontWeight: 700 }}>
              {t('musicSystem.liveDestination.thisDevice')}
            </Typography>
            <Typography sx={{ color: MUTED, fontSize: 11 }}>
              {t('musicSystem.liveDestination.thisDeviceHint')}
            </Typography>
          </Box>
          <Radio
            checked={destKind === 'local'}
            onChange={selectLocalDevice}
            disabled={!localSelectable}
            size="small"
            sx={{ color: '#666', '&.Mui-checked': { color: SONOS_BLUE } }}
          />
        </Box>
      )}
      {controllable.map((sp) => {
        const row = localSpeakers.find((s) => s.id === sp.id) || sp;
        const checked = selected.includes(sp.id);
        const selectableRow = isSpeakerSelectable(sp);
        const readOnly = !selectableRow;
        const showCoordRadio = selected.filter((id) => id !== sp.id).length > 0 && checked;
        return (
          <Box
            key={sp.id}
            sx={{
              mb: 0.55,
              opacity: readOnly ? 0.45 : 1,
              px: 0.25,
              py: 0.55,
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
            }}
          >
            <SonosModelBadge modelo={row.modelo} />

            <Box sx={{ flex: 1, minWidth: 0, minHeight: SONOS_MODEL_BADGE_SIZE, position: 'relative', overflow: 'visible' }}>
              <Box sx={{
                position: 'absolute',
                top: '-8px',
                left: '10px',
                right: 0,
                display: 'flex',
                alignItems: 'center',
                gap: 0.35,
                minWidth: 0,
              }}>
                {showCoordRadio && (
                  <Radio
                    checked={coordinatorId === sp.id}
                    onChange={() => { userTouchedRef.current = true; setCoordinatorId(sp.id); }}
                    sx={{ color: SONOS_BLUE, p: 0, mr: -0.25 }}
                    size="small"
                  />
                )}
                <Typography sx={{
                  color: '#fff',
                  fontSize: 13,
                  fontWeight: checked ? 600 : 400,
                  lineHeight: 1.15,
                  flex: 1,
                  minWidth: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}>
                  {row.nombre}
                </Typography>
                {!row.online && (
                  <Typography sx={{ color: '#e57373', fontSize: 9, flexShrink: 0 }}>
                    {t('sonos.card.unavailable')}
                  </Typography>
                )}
              </Box>
              <Box sx={{
                position: 'absolute',
                top: '50%',
                left: '10px',
                right: 0,
                transform: 'translateY(-50%)',
              }}>
                <DestinationVolume
                  volume={row.volume ?? 0}
                  disabled={readOnly || !row.online}
                  onApplied={(vol) => { refreshVol(vol, row.id); }}
                />
              </Box>
            </Box>

            <RoundSonosCheckbox
              checked={checked}
              disabled={readOnly}
              onChange={() => toggleSelect(sp.id)}
            />
          </Box>
        );
      })}
    </Box>
  );

  const footer = (
    <Box sx={{
      flexShrink: 0,
      px: 1,
      py: 1,
      borderTop: `1px solid ${SURFACE_BORDER}`,
      background: HEADER_GRADIENT,
    }}>
      <Box sx={{ display: 'flex', gap: 0.75 }}>
        <Button
          startIcon={<CompareArrows sx={{ fontSize: 16 }} />}
          onClick={handleNormalize}
          disabled={busy || destKind === 'local' || selected.length === 0}
          sx={{
            ...PILL_BTN_SX,
            flex: 1,
            color: SONOS_BLUE,
            borderColor: SONOS_BLUE,
            border: `1px solid ${SONOS_BLUE}`,
            fontSize: 11,
            px: 0.5,
            '&:hover': { borderColor: '#1a7ae0', backgroundColor: 'rgba(35,145,255,0.08)' },
          }}
          variant="outlined"
        >
          {t('musicSystem.liveDestination.normalize')}
        </Button>
        <Button
          onClick={handleApply}
          disabled={busy || !canApply}
          sx={{
            ...PILL_BTN_SX,
            flex: 1,
            backgroundColor: SONOS_BLUE,
            color: '#fff',
            fontSize: 11,
            px: 0.5,
            '&:hover': { backgroundColor: '#1a7ae0' },
            '&.Mui-disabled': { backgroundColor: '#555', color: '#999' },
          }}
          variant="contained"
        >
          {busy ? t('musicSystem.liveDestination.applying', { defaultValue: 'Aplicando…' }) : t('musicSystem.liveDestination.apply')}
        </Button>
      </Box>
      {applyError && (
        <Typography sx={{ color: '#e57373', fontSize: 11, textAlign: 'center', px: 0.5, mt: 0.75 }}>
          {applyError}
        </Typography>
      )}
    </Box>
  );

  return (
    <SonosSideSlidePanel
      ref={panelRef}
      open={open}
      onClose={onClose}
      testId="live-destination-modal"
      title={t('musicSystem.liveDestination.title')}
      toolbar={toolbar}
      footer={footer}
      zIndex={1600}
    >
      {speakerList}
    </SonosSideSlidePanel>
  );
};

export default LiveDestinationModal;
